// The app's state, typed, and the state a freshly opened dataset starts from.
// Pure, DOM-free. main.ts holds the one instance and renders from it.
import type { ChartLine } from "./chartmodel";
import { defaultSelection, splitDimOf, type Combo, type Meta, type Selection, type Translated } from "./meta";
import type { CatalogItem, DatasetWords, MapItem, SubSeries } from "./search";
import type { BrowseTab } from "./route";
import { defaultTransform } from "./transforms";

export type ViewName = "landing" | "loading" | "results" | "detail";

/** A comparison series from another dataset, drawn over the open one. */
export interface Extra {
  datasetId: string;
  datasetTitle: string;
  badge: string;
  /** The overlay's series: its dataset's split dim and code. Absent for a
   * whole dataset whose meta was not loaded. */
  dim?: string | null;
  code?: string | null;
  label?: string;
  /** A sub-series (search hit) rather than a dataset's default series. */
  isSub: boolean;
}

export interface OpenDataset {
  id: string;
  meta: Meta;
  /** Data-bearing dim tuples, for greying out empty options; null until loaded. */
  combos: Combo[] | null;
}

/** Which rail cards are open. */
export interface OpenCards { breakdown: boolean; details: boolean; access: boolean; download: boolean }

export interface Category { key?: string; name?: Translated; [k: string]: unknown }

export interface AppState {
  // What the site offers, loaded at boot (subIndex on idle, maps when they land).
  catalog: CatalogItem[];
  categories: Category[];
  maps: MapItem[];
  subIndex: SubSeries[] | null;
  subReady: boolean;
  /** /search-words.json, loaded with subIndex; search works without it. */
  searchWords: Record<string, DatasetWords> | null;

  // Where the visitor is.
  view: ViewName;
  query: string;
  /** Rendered as an ?embed=1 iframe. */
  embed: boolean;
  /** Browse view topic groups the visitor folded. */
  closedGroups: Set<string>;
  /** The browse view's tab. "maps" has its own URL (lib/route mapsTabPath). */
  tab: BrowseTab;

  // The open dataset and what its chart is asked to show.
  current: OpenDataset | null;
  splitDim: string | null;
  selection: Selection;
  /** Hierarchy nodes open in each Breakdown tree, per dim. */
  expanded: Record<string, Set<string>>;
  transform: string;
  /** "all", a preset in years ("1", "5", "10") or "custom" (a drag-zoom). */
  range: string;
  /** A user title for the chart; null = the dataset's own. */
  title: string | null;
  /** A window from a shared URL, applied at the next draw (display seconds). */
  pendingRange: { min: number; max: number } | null;
  extras: Extra[];
  /** The selection is the dataset default, not one the visitor picked. */
  autoDefault: boolean;
  /** The default drew nothing once and was replaced (fallbackSelect); not again. */
  fellBack: boolean;
  openCards: OpenCards;
  /** Code tab in the "Use this series" card. */
  access: string;

  // What the chart drew (the uPlot instance itself lives in src/chart.ts).
  lastSeries: { dates: string[]; series: ChartLine<Extra>[] } | null;
  /** Ratio-of-GDP levels shown as a percentage axis. */
  pctMode: boolean;
}

export function initialState(): AppState {
  return {
    catalog: [], categories: [], maps: [], subIndex: null, subReady: false, searchWords: null,
    view: "landing", query: "", embed: false, closedGroups: new Set(), tab: "datasets",
    current: null, splitDim: null, selection: {}, expanded: {},
    transform: "level", range: "all", title: null, pendingRange: null, extras: [],
    autoDefault: false, fellBack: false,
    openCards: { breakdown: true, details: false, access: false, download: false }, access: "r",
    lastSeries: null, pctMode: false,
  };
}

/** The fields a freshly opened dataset starts from: its default selection (or
 * a sub-series search hit, `preselect`), level view, full range, no overlays
 * or title, and the datasheet's opening transform. The Details card starts open when there is no Breakdown to show.
 * `lastSeries` is cleared so nothing (zoom, share URL) reads the previous
 * dataset's lines. */
export function openedState(meta: Meta, preselect?: { dim?: string; code?: string }) {
  const hasDims = (meta.dim_order || []).length > 0;
  return {
    lastSeries: null,
    splitDim: splitDimOf(meta),
    selection: defaultSelection(meta, preselect),
    expanded: {},
    transform: defaultTransform(meta.frequency, meta.default_transform),
    range: "all",
    title: null,
    pendingRange: null,
    autoDefault: true,
    fellBack: false,
    extras: [],
    openCards: { breakdown: true, details: !hasDims, access: false, download: false },
  } satisfies Partial<AppState>;
}
